import type {
  QiCatalogProductDTO,
  QiCatalogRawMaterialDTO,
  QiCatalogServiceDTO,
} from "../api/catalog-types";

export type QiPriceTier = {
  id: string;
  name: string;
  minQuantity: number;
  unitPrice: number;
};

export type QiCatalogPricedItem = {
  priceTiers: QiPriceTier[] | null;
  unit: string;
};

function resolveUnitPriceForQuantity(
  quantity: number,
  tiers: QiPriceTier[] | null | undefined,
): number {
  const qty = Math.max(1, Math.floor(quantity));
  const applicable = (tiers ?? [])
    .filter((row) => qty >= row.minQuantity)
    .sort((a, b) => b.minQuantity - a.minQuantity);

  return applicable[0]?.unitPrice ?? 0;
}

export function resolveQiCatalogUnitPrice(
  item: QiCatalogPricedItem,
  qty: number,
): number {
  return resolveUnitPriceForQuantity(qty, item.priceTiers);
}

export function resolveQiCatalogPriceHint(
  item: QiCatalogPricedItem,
  qty: number,
): string | null {
  const quantity = Math.max(1, Math.floor(Number(qty) || 1));
  const unitPrice = resolveQiCatalogUnitPrice(item, quantity);

  const sortedTiers = [...(item.priceTiers ?? [])].sort(
    (a, b) => b.minQuantity - a.minQuantity,
  );
  for (const tier of sortedTiers) {
    if (quantity >= tier.minQuantity && tier.unitPrice === unitPrice) {
      const unit = item.unit.trim() || "";
      return `${tier.name} · ≥ ${tier.minQuantity}${unit ? ` ${unitLabel}` : ""}`.replace("unitLabel", unit);
    }
  }

  return null;
}

export function resolveQiProductUnitPrice(
  product: QiCatalogProductDTO,
  qty: number,
) {
  return resolveQiCatalogUnitPrice(product, qty);
}

export function resolveQiProductPriceHint(
  product: QiCatalogProductDTO,
  qty: number,
): string | null {
  return resolveQiCatalogPriceHint(product, qty);
}

export function resolveQiRawMaterialUnitPrice(
  rawMaterial: QiCatalogRawMaterialDTO,
  qty: number,
) {
  return resolveQiCatalogUnitPrice(rawMaterial, qty);
}

export function resolveQiRawMaterialPriceHint(
  rawMaterial: QiCatalogRawMaterialDTO,
  qty: number,
): string | null {
  return resolveQiCatalogPriceHint(rawMaterial, qty);
}

export function resolveQiServiceUnitPrice(
  service: QiCatalogServiceDTO,
  qty: number,
) {
  return resolveQiCatalogUnitPrice(service, qty);
}

export function resolveQiServicePriceHint(
  service: QiCatalogServiceDTO,
  qty: number,
): string | null {
  return resolveQiCatalogPriceHint(service, qty);
}

export function resolveQiBaseUnitPrice(item: QiCatalogPricedItem): number {
  return resolveQiCatalogUnitPrice(item, 1);
}
